import { FunctionComponent } from 'react';
import { YourDetailsTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { useUserData } from '@hooks';
import {
  msalInstance,
  handleLoginPageEvent,
  yourDetailsSchema,
  parseZodErrors,
  LoginRequestConfig,
  handleLoginRequest,
  yourDetailsSchemaWithPhoneNumber,
} from '@utils';
import { ChangeEvent, useEffect, useState } from 'react';
import { ZodError } from 'zod';
import {
  useSignsBollardsStore,
  useHandleSubmit,
  useSignsBollardsPayload,
} from '../../../../hooks';

type Contact = {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
};

export const YourDetails: FunctionComponent = () => {
  const navigate = useNavigate();
  const payload = useSignsBollardsPayload();
  const { mutateAsync: submitForm, isPending } = useHandleSubmit({
    confirmationRoute: RoutesList.CONFIRMATION,
    endpoint: '/report/v1/roads-and-streets/signs-and-bollards',
  });
  const { update, data } = useSignsBollardsStore();

  const INCLUDE_PHONE_NUMBER = data.contactUserOption === 'Yes';

  const formData: Contact = {
    firstName: data.userDetails.firstName,
    lastName: data.userDetails.lastName,
    email: data.userDetails.email,
  };

  if (INCLUDE_PHONE_NUMBER) {
    formData['phoneNumber'] = data.userDetails.phoneNumber;
  }
  const [crmId, setCrmId] = useState<string>('');
  const [idProvider, setIdProvider] = useState<string>('');
  const { isLoggedInUser, contactData, isLoading, isFetching } =
    useUserData(crmId);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const isLoadingUserData = isLoading || isFetching;

  useEffect(() => {
    if (!crmId) {
      handleLoginPageEvent(msalInstance, setCrmId, setIdProvider);
    }

    if (contactData) {
      const contact: Contact = {
        email: contactData.emailaddress1 || '',
        firstName: contactData.firstname || '',
        lastName: contactData.lastname || '',
      };
      update('userDetails.firstName', contact.firstName);
      update('userDetails.lastName', contact.lastName);
      update('userDetails.email', contact.email);

      if (INCLUDE_PHONE_NUMBER && contact.phoneNumber) {
        update('userDetails.phoneNumber', contact.phoneNumber);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactData, crmId, idProvider]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    if (name === 'firstName') {
      update('userDetails.firstName', value);
    } else if (name === 'lastName') {
      update('userDetails.lastName', value);
    } else if (name === 'email') {
      update('userDetails.email', value);
    } else if (name === 'phoneNumber') {
      update('userDetails.phoneNumber', value);
    }
    setErrorMessages([]);
  };

  const handleSubmit = async () => {
    const { firstName, lastName, email, phoneNumber } = data.userDetails;
    const containsPhoneNumber = (phoneNumber?.length || 0) > 0;
    try {
      if (INCLUDE_PHONE_NUMBER && containsPhoneNumber) {
        yourDetailsSchemaWithPhoneNumber.parse({
          firstName,
          lastName,
          email,
          phoneNumber,
        });
      } else {
        yourDetailsSchema.parse({ firstName, lastName, email });
      }

      await submitForm({ payload, isLoggedInUser });
      navigate(RoutesList.CONFIRMATION);
    } catch (error) {
      if (error instanceof ZodError) {
        const newErrorMessages = parseZodErrors(error);
        setErrorMessages(newErrorMessages);
      }
    }
  };

  const loginConfig: LoginRequestConfig = {
    msal: msalInstance,
    navigate: () => navigate(RoutesList.YOUR_DETAILS, { replace: true }),
  };

  const handleLogin = () => handleLoginRequest(loginConfig);

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.CONTACT_USER_OPTION);
    },
  };

  const buttonNext = {
    label: 'Submit',
    loading: isPending,
    onClick: handleSubmit,
  };

  return (
    <YourDetailsTemplate
      isLoadingUserData={isLoadingUserData}
      isLoggedInUser={isLoggedInUser}
      userData={formData}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      errorMessages={errorMessages}
      handleLogin={handleLogin}
      onChange={handleChange}
      idProvider={idProvider}
      isContactNumberOptional={true}
    />
  );
};
